import type { Food, MealType } from '@fitness/api-client';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/Button';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { Badge } from '@/components/exercise/Chip';
import { FoodForm } from '@/components/food/FoodForm';
import { FoodThumb } from '@/components/food/FoodRow';
import { NutritionTable } from '@/components/food/NutritionTable';
import { QuantitySheet } from '@/components/food/QuantitySheet';
import { foodDetailSchema, type FoodDetailSearch } from '@/components/food/searchParams';
import { todayIso } from '@/components/food/format';
import { describeError, hasErrorCode } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { OFF_ATTRIBUTION, foodSourceLabel } from '@/lib/i18n/nutrition';
import { useDeleteFood, useFood, useRefreshFood, useUpdateFood } from '@/lib/queries/foods';

export const Route = createFileRoute('/_app/food/foods/$id')({
  validateSearch: (search: Record<string, unknown>): FoodDetailSearch =>
    foodDetailSchema.parse(search),
  component: FoodDetailPage,
});

function FoodDetailPage() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const query = useFood(id);
  const date = search.date ?? todayIso();

  return (
    <>
      <PageHeader
        title={query.data?.name ?? 'Alimento'}
        {...(query.data?.brand ? { subtitle: query.data.brand } : {})}
        action={
          <Link
            to="/food"
            search={{ date }}
            aria-label="Volver al diario"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised active:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      {query.isPending ? (
        <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface-raised p-4 text-ink-muted">
          <Spinner size="sm" />
          <span className="text-sm">Cargando el alimento…</span>
        </div>
      ) : query.isError ? (
        <ErrorBanner
          title="No hemos podido cargar el alimento"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <FoodDetail
          food={query.data}
          date={date}
          meal={search.meal ?? 'lunch'}
          mealLocked={search.meal !== undefined}
        />
      )}
    </>
  );
}

interface FoodDetailProps {
  food: Food;
  date: string;
  meal: MealType;
  mealLocked: boolean;
}

function FoodDetail({ food, date, meal, mealLocked }: FoodDetailProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const refresh = useRefreshFood(food.id);
  const isMine = food.ownerId !== null && food.ownerId === user?.id;

  if (editing) {
    return <FoodEditor food={food} onDone={() => setEditing(false)} />;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-4">
        <FoodThumb imageUrl={food.imageUrl} className="size-20" />
        <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
          <Badge tone={isMine ? 'brand' : 'plain'}>{foodSourceLabel(food.source)}</Badge>
          {food.nutriscore !== null ? (
            <Badge>Nutri-Score {food.nutriscore.toUpperCase()}</Badge>
          ) : null}
          {food.novaGroup !== null ? <Badge>NOVA {food.novaGroup}</Badge> : null}
        </div>
      </div>

      <Button size="lg" block icon={<Plus className="size-5" />} onClick={() => setAdding(true)}>
        Añadir al diario
      </Button>

      <NutritionTable food={food} />

      {food.source === 'off' ? (
        <section className="flex flex-col gap-2 border-t border-line pt-4">
          <p className="text-xs text-ink-faint">{OFF_ATTRIBUTION}</p>
          <Button
            variant="secondary"
            icon={<RefreshCw className="size-4" />}
            loading={refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            Actualizar desde la fuente
          </Button>
          {refresh.isError ? (
            <p role="alert" className="text-sm text-danger">
              {hasErrorCode(refresh.error, 'upstream_unavailable')
                ? 'Open Food Facts no responde, prueba más tarde.'
                : describeError(refresh.error)}
            </p>
          ) : null}
        </section>
      ) : null}

      {isMine ? <OwnerActions food={food} onEdit={() => setEditing(true)} /> : null}

      {adding ? (
        <QuantitySheet
          open
          onClose={() => setAdding(false)}
          target={{ kind: 'food', food }}
          date={date}
          meal={meal}
          mealLocked={mealLocked}
          onSaved={() => void navigate({ to: '/food', search: { date } })}
        />
      ) : null}
    </div>
  );
}

function FoodEditor({ food, onDone }: { food: Food; onDone: () => void }) {
  const update = useUpdateFood(food.id);
  return (
    <FoodForm
      food={food}
      submitting={update.isPending}
      errorTitle="No se han podido guardar los cambios"
      errorMessage={update.isError ? describeError(update.error, { saving: true }) : undefined}
      submitLabel="Guardar cambios"
      onCancel={onDone}
      onSubmit={(input) => update.mutate(input, { onSuccess: onDone })}
    />
  );
}

/** Editar y borrar, sólo para los alimentos que ha creado el usuario. */
function OwnerActions({ food, onEdit }: { food: Food; onEdit: () => void }) {
  const navigate = useNavigate();
  const remove = useDeleteFood(food.id);
  const [confirming, setConfirming] = useState(false);
  const inUse = remove.isError && hasErrorCode(remove.error, 'food_in_use');

  return (
    <section className="flex flex-col gap-2 border-t border-line pt-4">
      <p className="text-xs text-ink-faint">Este alimento es tuyo.</p>
      <div className="flex gap-2">
        <Button variant="secondary" icon={<Pencil className="size-4" />} onClick={onEdit}>
          Editar
        </Button>
        {confirming ? null : (
          <Button
            variant="ghost"
            icon={<Trash2 className="size-4" />}
            onClick={() => setConfirming(true)}
          >
            Eliminar
          </Button>
        )}
      </div>

      {confirming ? (
        <div className="rounded-xl border border-danger/40 bg-danger-surface p-3">
          <p className="text-sm">
            ¿Seguro que quieres eliminar <strong>{food.name}</strong>? Lo que ya has apuntado en el
            diario se conserva.
          </p>
          {remove.isError ? (
            <p role="alert" className="mt-2 text-sm text-danger">
              {inUse
                ? 'Alguna receta usa este alimento como ingrediente; quítalo de ahí antes de borrarlo.'
                : describeError(remove.error)}
            </p>
          ) : null}
          <div className="mt-3 flex gap-2">
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() =>
                remove.mutate(undefined, {
                  onSuccess: () => {
                    void navigate({ to: '/food', search: {} });
                  },
                })
              }
            >
              Sí, eliminar
            </Button>
            <Button
              variant="secondary"
              disabled={remove.isPending}
              onClick={() => setConfirming(false)}
            >
              Cancelar
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
